import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Mic, PenSquare } from "lucide-react";
import GlassCard from "../components/GlassCard";
import PageShell from "../components/PageShell";
import { useAuth } from "../lib/auth";

const NewSession = () => {
  const nav = useNavigate();
  const { user } = useAuth();
  const [clientName, setClientName] = useState("");
  const [error, setError] = useState("");

  const isClient = user?.role === "client";
  const fieldLabel = isClient ? "Session label" : "Client name";
  const fieldPlaceholder = isClient ? "e.g. Therapy Session, Tuesday Reflection" : "Type or select";
  const subhead = isClient
    ? "How would you like to capture today's session?"
    : "How would you like to capture today's session?";

  const proceed = (mode) => {
    const value = clientName.trim() || (isClient ? "Personal session" : "");
    if (!value) {
      setError(isClient ? "Please add a label for this session." : "Please enter a client name first.");
      return;
    }
    sessionStorage.setItem("well_client_name", value);
    if (mode === "ai") nav("/record");
    else nav("/write-notes");
  };

  return (
    <PageShell showSettings>
      <div className="pt-16 pb-32 max-w-3xl mx-auto flex flex-col items-center gap-10 well-page-enter">
        <h1 className="text-3xl sm:text-4xl font-bold text-[#2A3B32] text-center">New Session</h1>

        <GlassCard className="w-full max-w-md">
          <label className="text-sm font-medium text-[#4A5D52]">{fieldLabel}</label>
          <input
            data-testid="new-session-client-name"
            value={clientName}
            onChange={(e) => { setClientName(e.target.value); setError(""); }}
            placeholder={fieldPlaceholder}
            className="mt-2 w-full h-[52px] bg-white/60 backdrop-blur-sm border border-white/40 text-[#2A3B32] rounded-[16px] px-4 focus:outline-none focus:ring-2 focus:ring-[#8BA38E]"
          />
          {error && <p className="mt-2 text-sm text-[#B85C5C]" data-testid="new-session-error">{error}</p>}
        </GlassCard>

        <p className="text-base text-[#4A5D52] text-center max-w-md">{subhead}</p>

        <div className="w-full max-w-3xl grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
          <button
            onClick={() => proceed("ai")}
            data-testid="choose-ai-listen-card"
            className="text-left group"
          >
            <GlassCard className="h-full hover:bg-white/55 transition-all hover:scale-[1.02] hover:shadow-[0_12px_40px_rgba(139,163,142,0.18)]">
              <div className="flex flex-col items-center text-center gap-4 py-6">
                <div className="w-16 h-16 rounded-full bg-[#8BA38E] flex items-center justify-center shadow-[0_4px_16px_rgba(139,163,142,0.35)]">
                  <Mic size={28} strokeWidth={1.6} className="text-white" />
                </div>
                <h3 className="text-xl font-semibold text-[#2A3B32]">AI Listen In</h3>
                <p className="text-sm text-[#4A5D52] leading-relaxed">
                  Let AI transcribe and summarize your session.
                </p>
              </div>
            </GlassCard>
          </button>

          <button
            onClick={() => proceed("write")}
            data-testid="choose-write-notes-card"
            className="text-left group"
          >
            <GlassCard className="h-full hover:bg-white/55 transition-all hover:scale-[1.02] hover:shadow-[0_12px_40px_rgba(139,163,142,0.18)]">
              <div className="flex flex-col items-center text-center gap-4 py-6">
                <div className="w-16 h-16 rounded-full bg-white/70 border border-[#8BA38E]/40 flex items-center justify-center shadow-[0_4px_16px_rgba(139,163,142,0.18)]">
                  <PenSquare size={28} strokeWidth={1.6} className="text-[#6A7F6C]" />
                </div>
                <h3 className="text-xl font-semibold text-[#2A3B32]">Write Notes</h3>
                <p className="text-sm text-[#4A5D52] leading-relaxed">
                  Type your own session notes manually.
                </p>
              </div>
            </GlassCard>
          </button>
        </div>
      </div>
    </PageShell>
  );
};

export default NewSession;
